import ScreenWrapper from "@/components/ScreenWrapper"
import { checkPermissions } from "@/utils/checkPermissions"
import {
  createFileRoute,
  redirect,
  useParams,
  useSearch,
} from "@tanstack/react-router"
import { z } from "zod"
import { facturationMenuItems } from "@/constants/navigation"
import { useGenerateInvoice } from "@/hooks/invoices/useGenerateInvoice"
import { LessonsTable } from "@/components/invoices/LessonsTable"
import { TeachingMaterialsTable } from "@/components/invoices/TeachingMaterialsTable"
import { InvoiceSummaryPanel } from "@/components/invoices/InvoiceSummaryPanel"
import { invoiceListQueryKeys } from "@/api/query-keys/invoices"
import { getFilteredInvoices } from "@/api/services/invoices"
import { InvoiceStatus } from "@/api/types/invoices"

const searchSchema = z.object({
  spoId: z.number(),
  studentName: z.string(),
  paymentSource: z.enum(["duo", "self"]),
  conceptInvoiceId: z.number().optional(),
})

export const Route = createFileRoute(
  "/facturatie/cursisten/$studentId/factuur-genereren",
)({
  component: RouteComponent,
  validateSearch: searchSchema,
  beforeLoad: async ({ search, context }) => {
    checkPermissions("read", "invoices")

    if (search.conceptInvoiceId !== undefined) return

    const filters = {
      page: 1,
      pageSize: 1,
      studentProjectOrganizationId: search.spoId,
      filters: { status: ["concept"] as InvoiceStatus[] },
    }

    const result = await context.queryClient.fetchQuery({
      queryKey: invoiceListQueryKeys.filters(filters),
      queryFn: () => getFilteredInvoices(filters),
    })

    const existing = result.data.invoices[0]
    if (existing && existing.studentProjectOrganizationId === search.spoId) {
      throw redirect({
        search: { ...search, conceptInvoiceId: existing.id },
        replace: true,
      })
    }
  },
})

function RouteComponent() {
  const { studentId } = useParams({
    from: "/facturatie/cursisten/$studentId/factuur-genereren",
  })
  const { spoId, studentName, paymentSource, conceptInvoiceId } = useSearch({
    from: "/facturatie/cursisten/$studentId/factuur-genereren",
  })

  return (
    <ScreenWrapper
      showManagementMenu
      managementMenuItemsFromProps={facturationMenuItems}
    >
      <FactuurGenereren
        key={spoId}
        studentId={studentId}
        spoId={spoId}
        studentName={studentName}
        paymentSource={paymentSource}
        conceptInvoiceId={conceptInvoiceId}
      />
    </ScreenWrapper>
  )
}

function FactuurGenereren({
  studentId,
  spoId,
  studentName,
  paymentSource,
  conceptInvoiceId,
}: {
  studentId: string
  spoId: number
  studentName: string
  paymentSource: "duo" | "self"
  conceptInvoiceId: number | undefined
}) {
  const {
    lessons,
    lessonsLoading,
    materials,
    materialsLoading,
    selectedIds,
    selectedMaterialIds,
    selectedMaterials,
    conceptInvoice,
    hasChanges,
    isGenerating,
    isRegenerating,
    isSending,
    handleToggleLesson,
    handleToggleAllLessons,
    handleToggleMaterial,
    handleToggleAllMaterials,
    handleGenerate,
    handleRegenerate,
    handleSend,
  } = useGenerateInvoice(spoId, conceptInvoiceId)

  return (
    <div className="py-8">
      <h1 className="text-2xl font-bold mb-6">Factuur genereren</h1>

      <div className="flex gap-8 items-start">
        <div className="flex-1 min-w-0 flex flex-col gap-6">
          <LessonsTable
            lessons={lessons}
            isLoading={lessonsLoading}
            selectedIds={selectedIds}
            onToggleOne={handleToggleLesson}
            onToggleAll={handleToggleAllLessons}
          />

          <TeachingMaterialsTable
            materials={materials}
            isLoading={materialsLoading}
            selectedIds={selectedMaterialIds}
            onToggleOne={handleToggleMaterial}
            onToggleAll={handleToggleAllMaterials}
          />
        </div>

        <InvoiceSummaryPanel
          studentId={studentId}
          studentName={studentName}
          paymentSource={paymentSource}
          selectedCount={selectedIds.size}
          selectedMaterials={selectedMaterials}
          conceptInvoice={conceptInvoice}
          hasChanges={hasChanges}
          isGenerating={isGenerating}
          isRegenerating={isRegenerating}
          isSending={isSending}
          onGenerate={handleGenerate}
          onRegenerate={handleRegenerate}
          onSend={handleSend}
        />
      </div>
    </div>
  )
}
